import { useMemo } from 'react';
import { usePublicSettings } from '../services/httpServices/queries';
import { DEFAULT_HOSPITAL_TIMEZONE } from '../lib/timezones';

interface HospitalTimezone {
  /** IANA zone name to format dates in (Settings → General). */
  timezone: string;
  /** False while the setting is still loading; defaults are already available. */
  isReady: boolean;
}

/**
 * The hospital's IANA timezone (Settings → General → `hospital_timezone`).
 *
 * Read from the public settings endpoint so every role can resolve the
 * hospital's calendar day — not just users allowed to open Settings. Falls back
 * to Asia/Dhaka, mirroring the backend default, until the value loads.
 *
 * Callers that *seed* a date range must wait for `isReady`; otherwise they may
 * compute "today" with the fallback zone and then keep it after the real zone
 * arrives.
 */
export function useHospitalTimezone(): HospitalTimezone {
  const { data, isLoading } = usePublicSettings();
  return useMemo(() => {
    const value = (data?.data?.hospital_timezone || '').trim();
    return {
      timezone: value || DEFAULT_HOSPITAL_TIMEZONE,
      isReady: !isLoading,
    };
  }, [data, isLoading]);
}
